import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialEventCustomPdfAppearanceBuyableItem = {
  eventCustomPdfAppearanceEvent?: { id: string } | null;
};

type EventCustomPdfAppearanceBuyableItem = {
  eventCustomPdfAppearanceEvent: { id: string };
};

type EventCustomPdfAppearanceWithEvent = {
  eventCustomPdfAppearanceEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "eventCustomPdfAppearance";

export const EventCustomPdfAppearanceBuyableItemHandler = {
  displayName: "Personnalisation des billets et badges",
  isCumulative: false,
  accounting: {
    generalAccountCode: "706510",
    invoiceLabel: "Personnalisation des billets et badges",
  },

  getEvent(item: EventCustomPdfAppearanceWithEvent): BuyableItemEvent {
    return item.eventCustomPdfAppearanceEvent;
  },

  isValid(item: PartialEventCustomPdfAppearanceBuyableItem): boolean {
    return !!item.eventCustomPdfAppearanceEvent?.id;
  },

  findEquivalent(
    item: PartialEventCustomPdfAppearanceBuyableItem,
  ): Prisma.BuyableItemWhereInput {
    return {
      eventCustomPdfAppearanceEvent: {
        id: item.eventCustomPdfAppearanceEvent?.id,
      },
    };
  },

  create({
    eventCustomPdfAppearanceEvent,
  }: EventCustomPdfAppearanceBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      eventCustomPdfAppearanceEvent: {
        connect: {
          id: eventCustomPdfAppearanceEvent.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!EventCustomPdfAppearanceBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for eventCustomPdfAppearance: ${groupKey}`);
    }

    const [, eventId] = groupKey.split("|");
    return {
      eventCustomPdfAppearanceEvent: {
        id: eventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialEventCustomPdfAppearanceBuyableItem): string {
    const eventId = item.eventCustomPdfAppearanceEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid eventCustomPdfAppearance item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "eventCustomPdfAppearance",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      eventCustomPdfAppearanceEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
